import React, { useEffect, useState } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import CustomerCombobox from "@/components/forms/CustomerCombobox";
import PhoneInput from "@/components/PhoneInput";
import { toast } from "sonner";

/* ------------------- NewOrderDialog: pick / create the guest first -------------------
   Mirrors the booking-calendar flow: live typeahead against the customer DB;
   an unmatched name becomes a new profile (email OR WhatsApp mandatory).
   Purchases (products / gift cards / series) attach to the guest without
   touching the booking calendar. Walk-in stays available for anonymous
   counter sales.

   Iter-317 · Delayed Order Creation. The "Register Sale" button no
   longer POSTs `/orders` on click — it only hands the resolved
   `customer` (or `null` for Walk-in) up to the parent, which stages
   a **pending sale** in memory. The DB record + `ORD-…` number are
   generated by the parent's item-add handler the moment the first
   line item is added. If the operator navigates away before adding
   an item, no order record ever exists. */
export default function NewOrderDialog({ open, onOpenChange, onStart, branchId = null }) {
  const [client, setClient] = useState(null);        // existing profile OR {__new:true}
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setClient(null); setFirstName(""); setLastName(""); setEmail(""); setWhatsapp(""); setBusy(false);
  }, [open]);

  const pick = (c) => {
    setClient(c);
    if (c?.__new) {
      const parts = (c.full_name || "").split(" ");
      setFirstName(parts[0] || "");
      setLastName(parts.slice(1).join(" "));
    }
  };

  const start = async () => {
    if (!client) return toast.error("Search and pick a guest, or create a new one.");
    if (!client.__new) { setBusy(true); try { await onStart(client); } finally { setBusy(false); } return; }
    // New profile — same rule as the customer DB: email OR WhatsApp required.
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
    if (!fullName) return toast.error("Enter the guest's name.");
    if (!email.trim() && !whatsapp.trim()) return toast.error("Email or WhatsApp is required for a new guest.");
    setBusy(true);
    try {
      const r = await staffApi.post("/customers", {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim() || null,
        whatsapp: whatsapp.trim() || null,
        // Counter sales create the profile before any appointment exists —
        // stamp the branch so the guest shows up under this location.
        branch_id: branchId || null,
      });
      const saved = r.data?.customer || r.data;
      toast.success(`New guest profile created — ${saved.full_name || fullName}`);
      await onStart(saved);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="new-order-dialog">
        <DialogHeader>
          <DialogTitle>New Order</DialogTitle>
          <DialogDescription>Who is this sale for? Search the guest database — an unmatched name becomes a new profile. The Order&nbsp;# is generated only after the first item is added.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <CustomerCombobox
            value={client && !client.__new ? client : null}
            onPick={pick}
            onClear={() => setClient(null)}
            allowCreate
            testId="new-order-customer"
          />
          {client?.__new && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3 space-y-2" data-testid="new-order-new-client">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-emerald-900 font-semibold">New guest profile</p>
                <button type="button" className="text-xs text-muted-foreground hover:underline" onClick={() => setClient(null)} data-testid="new-order-new-clear">change</button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name *" className="h-9" data-testid="new-order-first-name" />
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" className="h-9" data-testid="new-order-last-name" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="h-9" data-testid="new-order-email" />
                <PhoneInput value={whatsapp} onChange={setWhatsapp} testid="new-order-phone" placeholder="WhatsApp / phone" />
              </div>
              <p className="text-[11px] text-muted-foreground">Email <span className="font-medium">or</span> WhatsApp is required.</p>
            </div>
          )}
        </div>
        <DialogFooter className="flex items-center sm:justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={() => onStart(null)} disabled={busy} data-testid="new-order-walkin-btn" className="text-muted-foreground">
            Continue as Walk-in
          </Button>
          <Button onClick={start} disabled={busy} data-testid="new-order-register-sale-btn">
            {busy ? "Registering…" : "Register Sale"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
